/**
 * The field set for a task, shared by adding, editing, and resolving an inbox item.
 *
 * One component because they are one form. Three copies of "title, destination, energy,
 * due, flags" would drift, and the version you learn in one place should be the version you
 * meet in the others.
 */

import type { Area, Project } from "../api";
import { ENERGY_LEVELS, type TaskFieldValues } from "./taskFieldValues";
import { Die } from "./Die";
import { Calendar, Important, Repeat, Snooze, Star, Stopwatch } from "./icons";
import { useAutoGrow } from "./useAutoGrow";
import { describeRecurrence, type Freq, type RecurrenceFormValues } from "./recurrence";

/**
 * Activation cost, as a row of dice.
 *
 * Every button is the same width — including "unset", which gets an empty die frame rather
 * than a dash so it does not resize the row. The faces stay honest: ⚀ is energy 1, not
 * "unset", because the same glyph appears on the task row and two meanings for one symbol
 * is how a legend stops being learnable.
 */
export function EnergyPicker({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  return (
    <span className="energy-picker" role="group" aria-label="Energy">
      <button
        type="button"
        className={`btn energy-pip${value === null ? " primary" : " ghost"}`}
        aria-pressed={value === null}
        aria-label="No energy set"
        title="Not set - never hidden by the slider"
        onClick={() => onChange(null)}
      >
        <Die value={null} size={18} />
      </button>
      {ENERGY_LEVELS.map((level) => (
        <button
          key={level}
          type="button"
          className={`btn energy-pip${value === level ? " primary" : " ghost"}`}
          aria-pressed={value === level}
          aria-label={`Energy ${level}`}
          title={`Activation cost ${level} of 5`}
          onClick={() => onChange(level)}
        >
          <Die value={level} size={18} />
        </button>
      ))}
    </span>
  );
}

export function FlagButtons({
  important,
  starred,
  onImportant,
  onStarred,
  hideStar = false,
}: {
  important: boolean;
  starred: boolean;
  onImportant: (v: boolean) => void;
  onStarred: (v: boolean) => void;
  /** A subtask can never be starred (§8.6) — parents only. Rejected server-side either way;
   * this just keeps the control from offering something that always fails. */
  hideStar?: boolean;
}) {
  // A group of its own, not a fragment: as loose siblings the two could be split across a
  // wrap, and "important" and "star" are a pair you read together.
  return (
    <span className="flag-buttons" role="group" aria-label="Flags">
      <button
        type="button"
        className={`btn icon-toggle${important ? " primary" : " ghost"}`}
        aria-pressed={important}
        aria-label="Important"
        title="Important - always shows, whatever the energy slider says"
        onClick={() => onImportant(!important)}
      >
        <Important />
      </button>
      {!hideStar && (
        <button
          type="button"
          className={`btn icon-toggle${starred ? " primary" : " ghost"}`}
          aria-pressed={starred}
          aria-label="Star"
          title="Star - one of today's few"
          onClick={() => onStarred(!starred)}
        >
          <Star filled={starred} />
        </button>
      )}
    </span>
  );
}

export function DestinationPicker({
  value,
  areas,
  projects,
  onChange,
  allowInbox = false,
  resetPulse = false,
  fullWidth = false,
}: {
  value: string;
  areas: Area[];
  projects: Project[];
  onChange: (value: string) => void;
  allowInbox?: boolean;
  /** True for one brief moment right after *we* changed `value`, not the reader — a
   * remembered destination going stale (`destination.ts`) and snapping back to inbox on
   * its own. Unused by every caller except `QuickAddCard`; everyone else's picker only
   * ever changes because someone clicked it, which needs no announcing. */
  resetPulse?: boolean;
  /** Claims the whole row (`flex-basis: 100%`), pushing every sibling after it onto the
   * next line — `QuickAddCard` only, once energy/flags/the AI tier picker made that row too
   * crowded to share with a picker that also has to hold every area and project name.
   * Everyone else keeps the picker sized to its own content, alongside its row's other
   * fields. */
  fullWidth?: boolean;
}) {
  return (
    <select
      className={`input${resetPulse ? " dest-reset-pulse" : ""}`}
      style={fullWidth ? { flex: "1 1 100%", minWidth: 0 } : { flex: "1 1 10rem", minWidth: 0 }}
      aria-label="Area or project"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {allowInbox && <option value="">Inbox - decide later</option>}
      {areas.length > 0 && (
        <optgroup label="Areas">
          {areas.map((area) => (
            <option key={area.id} value={`area:${area.id}`}>
              {area.name}
            </option>
          ))}
        </optgroup>
      )}
      {projects.length > 0 && (
        <optgroup label="Projects">
          {[...projects]
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "accent" }))
            .map((project) => (
              <option key={project.id} value={`project:${project.id}`}>
                {project.name}
              </option>
            ))}
        </optgroup>
      )}
    </select>
  );
}

/**
 * The secondary fields — everything that is not title, destination or a flag.
 *
 * Collapsed by default when adding, because §8.2's fastest path in is title-enter-done and
 * a form with nine inputs is not that. Always open when editing, where you came
 * specifically to change something.
 */
export function DetailFields({
  values,
  onChange,
  hideRecurrence = false,
}: {
  values: TaskFieldValues;
  onChange: (patch: Partial<TaskFieldValues>) => void;
  /** A subtask can never be recurring (§8.6) — offering the control is a no-op the server
   * always rejects. */
  hideRecurrence?: boolean;
}) {
  const notes = useAutoGrow(values.notes);

  return (
    <>
      <textarea
        ref={notes}
        className="input notes"
        aria-label="Notes"
        placeholder="Notes"
        // Back to two: the floor for an empty box. It grows from here to fit (useAutoGrow).
        rows={2}
        value={values.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
      />

      {/*
        All four fields on one line when there is room, then 2 + 2, then one each.

        The pairs are what stop it going 3 + 1: each `.field-pair` is a single flex item, so
        the first thing that can break is the join between the pairs. They belong together
        anyway - due and until are both "when", estimate and repeat are both "how much".
      */}
      <div className="row wrap field-pairs">
        <span className="field-pair">
          <label className="small muted">
            <Calendar /> Due{" "}
            <input
              className="input field"
              type="date"
              aria-label="Due date"
              value={values.due}
              onChange={(e) => onChange({ due: e.target.value })}
            />
          </label>
          <label className="small muted" title="Hidden from the list until this date">
            <Snooze /> until{" "}
            <input
              className="input field"
              type="date"
              aria-label="Snoozed until"
              value={values.snoozed}
              onChange={(e) => onChange({ snoozed: e.target.value })}
            />
          </label>
        </span>

        <span className="field-pair">
          <label className="small muted">
            <Stopwatch /> Estimate{" "}
            <input
              className="input field narrow"
              type="number"
              min={0}
              placeholder="min"
              aria-label="Estimate in minutes"
              value={values.estimate}
              onChange={(e) => onChange({ estimate: e.target.value })}
            />
          </label>
        </span>
      </div>

      {!hideRecurrence && (
        <RecurrenceFields
          values={values.recurrence}
          recurStart={values.due}
          onChange={(patch) => onChange({ recurrence: { ...values.recurrence, ...patch } })}
        />
      )}
    </>
  );
}

/**
 * Every X days/weeks/months/years, specific weekdays, nth/last weekday of month, annually,
 * and an optional end condition (§4.6). Collapsed until the owner opts in.
 *
 * `recurStart` is the task's own `due` field, not a control of its own (DECISIONS.md D83) —
 * the rule expands from the date already on the form, under a name people already recognise.
 */
const WEEKDAYS: [string, string][] = [
  ["MO", "Mon"],
  ["TU", "Tue"],
  ["WE", "Wed"],
  ["TH", "Thu"],
  ["FR", "Fri"],
  ["SA", "Sat"],
  ["SU", "Sun"],
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function RecurrenceFields({
  values,
  recurStart,
  onChange,
}: {
  values: RecurrenceFormValues;
  /** The task's `due` field, doubling as the rule's start date (DECISIONS.md D83). */
  recurStart: string;
  onChange: (patch: Partial<RecurrenceFormValues>) => void;
}) {
  if (!values.enabled) {
    return (
      <button
        type="button"
        className="btn ghost small"
        onClick={() => onChange({ enabled: true })}
      >
        <Repeat /> Make this repeat
      </button>
    );
  }

  const unit =
    values.freq === "DAILY" ? "day" : values.freq === "WEEKLY" ? "week" : values.freq === "MONTHLY" ? "month" : "year";

  return (
    <div className="recurrence-fields">
      <div className="row wrap">
        <select
          className="input"
          aria-label="Frequency"
          value={values.freq}
          onChange={(e) => onChange({ freq: e.target.value as Freq })}
        >
          <option value="DAILY">Daily</option>
          <option value="WEEKLY">Weekly</option>
          <option value="MONTHLY">Monthly</option>
          <option value="YEARLY">Yearly</option>
        </select>
        <label className="small muted">
          every{" "}
          <input
            className="input field narrow"
            type="number"
            min={1}
            aria-label="Interval"
            value={values.interval}
            onChange={(e) => onChange({ interval: e.target.value })}
          />{" "}
          {unit}(s)
          {!recurStart && (
            <span className="small" style={{ color: "var(--warn)" }}>
              {" "}
              - set a due date to start it from
            </span>
          )}
        </label>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn ghost small" onClick={() => onChange({ enabled: false })}>
          Don't repeat
        </button>
      </div>

      {values.freq === "WEEKLY" && (
        <div className="row wrap" role="group" aria-label="Days of the week">
          {WEEKDAYS.map(([code, label]) => (
            <button
              key={code}
              type="button"
              className={`btn small${values.byweekday.includes(code) ? " primary" : " ghost"}`}
              aria-pressed={values.byweekday.includes(code)}
              onClick={() =>
                onChange({
                  byweekday: values.byweekday.includes(code)
                    ? values.byweekday.filter((d) => d !== code)
                    : [...values.byweekday, code],
                })
              }
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {values.freq === "MONTHLY" && (
        <div className="row wrap">
          <label className="small muted">
            <input
              type="radio"
              name="recur-month-mode"
              checked={values.monthMode === "day"}
              onChange={() => onChange({ monthMode: "day" })}
            />{" "}
            Day{" "}
            <input
              className="input field narrow"
              type="number"
              min={1}
              max={31}
              aria-label="Day of month"
              value={values.monthday}
              onChange={(e) => onChange({ monthday: e.target.value, monthMode: "day" })}
            />
          </label>
          <label className="small muted">
            <input
              type="radio"
              name="recur-month-mode"
              checked={values.monthMode === "weekday"}
              onChange={() => onChange({ monthMode: "weekday" })}
            />{" "}
            The{" "}
            <select
              className="input"
              aria-label="Which occurrence"
              value={values.weekdayOrdinal}
              onChange={(e) => onChange({ weekdayOrdinal: e.target.value, monthMode: "weekday" })}
            >
              <option value="1">first</option>
              <option value="2">second</option>
              <option value="3">third</option>
              <option value="4">fourth</option>
              <option value="-1">last</option>
            </select>{" "}
            <select
              className="input"
              aria-label="Weekday"
              value={values.weekday}
              onChange={(e) => onChange({ weekday: e.target.value, monthMode: "weekday" })}
            >
              {WEEKDAYS.map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {values.freq === "YEARLY" && (
        <div className="row wrap">
          <label className="small muted">
            On{" "}
            <select
              className="input"
              aria-label="Month"
              value={values.yearMonth}
              onChange={(e) => onChange({ yearMonth: e.target.value })}
            >
              {MONTHS.map((name, i) => (
                <option key={name} value={String(i + 1)}>
                  {name}
                </option>
              ))}
            </select>{" "}
            <input
              className="input field narrow"
              type="number"
              min={1}
              max={31}
              aria-label="Day of month"
              value={values.yearDay}
              onChange={(e) => onChange({ yearDay: e.target.value })}
            />
          </label>
        </div>
      )}

      <div className="row wrap" role="group" aria-label="When the next one is due">
        <label
          className="small muted"
          title="The next occurrence comes from the schedule, regardless of when this one was done - feed the dog, bin day"
        >
          <input
            type="radio"
            name="recur-anchor"
            checked={values.anchor === "schedule"}
            onChange={() => onChange({ anchor: "schedule" })}
          />{" "}
          By schedule
        </label>
        <label
          className="small muted"
          title="The next occurrence is measured from the day this one was actually done - air filter, oil change"
        >
          <input
            type="radio"
            name="recur-anchor"
            checked={values.anchor === "completion"}
            onChange={() => onChange({ anchor: "completion" })}
          />{" "}
          Since last done
        </label>
      </div>

      <div className="row wrap" role="group" aria-label="Ends">
        <label className="small muted">
          <input
            type="radio"
            name="recur-end"
            checked={values.endMode === "never"}
            onChange={() => onChange({ endMode: "never" })}
          />{" "}
          Never ends - a routine
        </label>
        <label className="small muted">
          <input
            type="radio"
            name="recur-end"
            checked={values.endMode === "until"}
            onChange={() => onChange({ endMode: "until" })}
          />{" "}
          Ends on{" "}
          <input
            className="input field"
            type="date"
            aria-label="End date"
            value={values.until}
            onChange={(e) => onChange({ until: e.target.value, endMode: "until" })}
          />
        </label>
        <label className="small muted">
          <input
            type="radio"
            name="recur-end"
            checked={values.endMode === "count"}
            onChange={() => onChange({ endMode: "count" })}
          />{" "}
          Ends after{" "}
          <input
            className="input field narrow"
            type="number"
            min={1}
            aria-label="Number of times"
            value={values.count}
            onChange={(e) => onChange({ count: e.target.value, endMode: "count" })}
          />{" "}
          times
        </label>
      </div>

      <p className="small muted" style={{ margin: 0 }}>
        {describeRecurrence(values)}
      </p>
    </div>
  );
}
